import { useEffect, useState } from 'react'
import { useAppDispatch } from './redux-hook'
import { AppSession } from 'app/app/api/auth/auth'
import { useSession } from 'next-auth/react'
import { updateActiveSession } from 'app/services/client/client-api'
import { t } from 'i18next'
import { hideAlert, showAlert } from '../redux/features/notification/alert-slice'
import { useSSEContext } from '../contexts/sse-context'
import { SessionChangedMessage } from 'app/app/api/sse/messages'
import { SSE_MESSAGE_TYPES_ENUM } from 'app/app/common/sse/sse-utils'
import { FALLBACK_TRANSLATION } from '../utils/client-constants'

// Todo: Localize the strings used in the alerts and sessions
export function useSessionSSE() {
  const dispatch = useAppDispatch()
  const { data: session } = useSession()
  const { subscribe, unsubscribe } = useSSEContext()
  const [isLoading, setIsLoading] = useState(false)

  const reclaimSession = async (userId: string, sessionId: string) => {
    try {
      const response = await updateActiveSession(userId, { userId, sessionId })
      if (response && response.success) {
        sessionStorage.setItem(`popup-shown`, 'false')
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error(`[SSE Hook] Error reclaiming session:`, error)
      return false
    }
  }

  useEffect(() => {
    const appSession = session as AppSession | undefined
    if (!appSession?.profile?.user_id || !appSession?.sessionId) {
      return
    }
    const {
      profile: { display_name: username, user_id: userId }
    } = appSession
    const sessionId = appSession.sessionId
    sessionStorage.setItem('sessionId', sessionId)
    console.log(`[SSE Hook] Registered session ID for user ${username}: ${sessionId}`)
    const handleSessionChanged = (message: SessionChangedMessage) => {
      const { sessionId: activeSessionId } = message
      const currentLocalSessionId = sessionStorage.getItem('sessionId')
      const hasShownPopupBefore = sessionStorage.getItem(`popup-shown`) === 'true'

      if (!currentLocalSessionId || !activeSessionId || activeSessionId === currentLocalSessionId) {
        sessionStorage.setItem(`popup-shown`, 'false')
        dispatch(hideAlert())
        return
      }

      if (hasShownPopupBefore) {
        console.log('[Session SSE] Popup already shown, ignoring duplicate message')
        return
      }

      sessionStorage.setItem(`popup-shown`, 'true')
      const { duplicateloginMessage, duplicateloginTitle, ok } = FALLBACK_TRANSLATION
      dispatch(
        showAlert({
          id: 'duplicate-login',
          title: t('multilogin.title') || duplicateloginTitle,
          description: t('multilogin.message', { username }) || duplicateloginMessage,
          primaryButtonContent: t('errors.modalOK') || ok,
          type: 'modal',
          onPrimaryClick: () => {
            setIsLoading(true)
            reclaimSession(userId, currentLocalSessionId!)
              .then(value => {
                if (value) {
                  dispatch(hideAlert())
                  sessionStorage.setItem(`popup-shown`, 'false')
                  setIsLoading(false)
                }
              })
              .catch(err => {
                setIsLoading(false)
                console.error(`[SSE Hook] Error reclaiming session on user action:`, err)
              })
          },
          deactivateCloseButton: true,
          isDismissOnBackdrop: false,
          isLoadingPrimaryButton: isLoading
        })
      )
    }

    subscribe(SSE_MESSAGE_TYPES_ENUM.SESSION_CHANGED, handleSessionChanged as any)

    return () => {
      unsubscribe(SSE_MESSAGE_TYPES_ENUM.SESSION_CHANGED, handleSessionChanged as any)
    }
  }, [dispatch, isLoading, session, subscribe, unsubscribe])
  return { reclaimSession }
}
